import { useCallback, useEffect, useRef, useState } from 'react';
import type { Meeting, SpeakerSegment } from '../../types';
import { t } from '../../lib/i18n';

interface MeetingMedia {
  transcript: SpeakerSegment[];
  audioUrl?: string;
  isLoading: boolean;
  error: string | null;
  /** Presigned ses adresinin süresi dolduysa yenisini ister (bir kez). */
  refreshAudio: () => void;
}

/**
 * Notun ses ve transkript kaynağı.
 *
 * Yerel (eski) toplantılarda veri zaten listede geliyor. S3'e arşivlenmiş
 * toplantılarda transkript listede yok; not açılınca ana süreç S3'ten
 * transkripti okuyup sesin süreli imzalı adresini döndürüyor.
 */
export function useMeetingMedia(meeting: Meeting): MeetingMedia {
  const isS3 = meeting.storage === 's3';
  const [state, setState] = useState<{
    transcript: SpeakerSegment[];
    audioUrl?: string;
    isLoading: boolean;
    error: string | null;
  }>({ transcript: [], isLoading: isS3, error: null });

  const refreshedRef = useRef(false);

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const media = await window.zabit.getMeetingMedia(meeting.id);
      setState({ transcript: media.transcript, audioUrl: media.audioUrl, isLoading: false, error: null });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: (err as Error).message || t('Toplantı dosyaları S3\'ten alınamadı.', 'Could not fetch the meeting files from S3.'),
      }));
    }
  }, [meeting.id]);

  useEffect(() => {
    refreshedRef.current = false;
    if (isS3) void load();
  }, [isS3, load]);

  const refreshAudio = useCallback(() => {
    if (!isS3 || refreshedRef.current) return;
    refreshedRef.current = true;
    void load();
  }, [isS3, load]);

  if (!isS3) {
    return {
      transcript: meeting.transcript,
      audioUrl: meeting.audioUrl,
      isLoading: false,
      error: null,
      refreshAudio,
    };
  }
  return { ...state, refreshAudio };
}
